import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Loader, Button, Icon, Segment, Message } from 'semantic-ui-react';
import GlassmorphismTheme from '../../../styles/glassmorphism';
import { ConversationService } from '../services/ConversationService';
import { Message as MessageType, ConversationPermissions } from '../types/conversation.types';
import OfferPreview from './OfferPreview';

interface MessageTimelineProps {
  clienteId: string;
  permissions: ConversationPermissions;
}

const MessageTimeline: React.FC<MessageTimelineProps> = ({ clienteId, permissions }) => {
  const [messages, setMessages] = useState<MessageType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // ===== LOAD MESSAGES =====
  const loadMessages = useCallback(async (newOffset: number = 0) => {
    setLoading(true);
    setError(null);
    try {
      const data = await ConversationService.getHistory(clienteId, 20, newOffset);
      if (newOffset === 0) {
        setMessages(data.mensagens);
      } else {
        setMessages(prev => [...prev, ...data.mensagens]);
      }
      setHasMore(data.mensagens.length === 20);
      setOffset(newOffset);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar mensagens');
    } finally {
      setLoading(false);
    }
  }, [clienteId]);

  // ===== INITIAL LOAD =====
  useEffect(() => {
    loadMessages(0);
  }, [clienteId, loadMessages]);

  // ===== AUTO SCROLL =====
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ===== FORMAT DATE =====
  const formatDate = (date: string) => {
    const d = new Date(date);
    return d.toLocaleDateString('pt-BR', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
  };

  // ===== FORMAT TIME =====
  const formatTime = (date: string) => {
    const d = new Date(date);
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  // ===== GROUP BY DATE =====
  const groupedMessages = messages.reduce((acc, msg) => {
    const date = formatDate(msg.data);
    if (!acc[date]) acc[date] = [];
    acc[date].push(msg);
    return acc;
  }, {} as Record<string, MessageType[]>);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {error && <Message error content={error} />}

      {loading && messages.length === 0 ? (
        <Loader active content="Carregando mensagens..." />
      ) : messages.length === 0 ? (
        <Message info content="Nenhuma mensagem ainda" />
      ) : (
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {/* LOAD MORE BUTTON */}
          {hasMore && messages.length > 0 && (
            <Button 
              fluid 
              basic 
              onClick={() => loadMessages(offset + 20)}
              loading={loading}
              style={{ marginBottom: GlassmorphismTheme.spacing.md }}
            >
              <Icon name="chevron up" /> Carregar Mais
            </Button>
          )}

          {/* MESSAGES GROUPED BY DATE */}
          {Object.entries(groupedMessages).map(([date, dayMessages]) => (
            <div key={date}>
              {/* DATE DIVIDER */}
              <div style={{
                textAlign: 'center',
                ...GlassmorphismTheme.components.divider,
              }}>
                <small style={{ 
                  color: '#7f8c8d',
                  background: 'rgba(255, 255, 255, 0.7)',
                  padding: '0 10px',
                  position: 'relative',
                  top: '-10px',
                }}>
                  {date}
                </small>
              </div>

              {/* MESSAGES */}
              {dayMessages.map(msg => (
                <div
                  key={msg.id}
                  style={{
                    marginBottom: GlassmorphismTheme.spacing.md,
                    display: 'flex',
                    justifyContent: msg.role === 'cliente' ? 'flex-end' : 'flex-start',
                  }}
                >
                  <Segment
                    style={{
                      maxWidth: '70%',
                      ...GlassmorphismTheme.components.glassCard,
                      color: '#2c3e50',
                      wordWrap: 'break-word',
                    }}
                  >
                    {/* OFFER PREVIEW */}
                    {msg.tipo === 'oferta' ? (
                      <OfferPreview offer={JSON.parse(msg.conteudo)} permissions={permissions} />
                    ) : (
                      <>
                        <p style={{ margin: '0 0 5px 0' }}>{msg.conteudo}</p>
                        {msg.audioUrl && (
                          <audio 
                            controls 
                            style={{ maxWidth: '100%', marginTop: '10px' }}
                          >
                            <source src={msg.audioUrl} type="audio/mpeg" />
                          </audio>
                        )}
                        {msg.anexoUrl && (
                          <a 
                            href={msg.anexoUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ display: 'block', marginTop: '10px' }}
                          >
                            <Icon name="file" /> Baixar anexo
                          </a>
                        )}
                      </>
                    )}
                    <small style={{
                      display: 'block',
                      marginTop: '8px',
                      opacity: 0.7,
                      textAlign: 'right',
                    }}>
                      {formatTime(msg.data)}
                    </small>
                  </Segment>
                </div>
              ))}
            </div>
          ))}

          {/* SCROLL ANCHOR */}
          <div ref={messagesEndRef} />
        </div>
      )}
    </div>
  );
};

export default MessageTimeline;
